import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import { notifications } from '@/mocks/dashboard';
import { mockGlobalEvents, mockSchoolHolidays, mockPersonalTasks, allCalendarEntries } from '@/mocks/calendarEntries';
import type { CalendarEntry, PersonalTask } from '@/mocks/calendarEntries';
import CalendarGrid from './components/CalendarGrid';
import CalendarList from './components/CalendarList';
import TaskModal from './components/TaskModal';
import { useAcl } from '@/hooks/useAcl';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

let taskIdCounter = 900;

export default function SchoolCalendarPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canManage } = useAcl();

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Filter visibility
  const [visibleTypes, setVisibleTypes] = useState({ global: true, holiday: true, task: true });

  // Task modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<PersonalTask | null>(null);

  // Data state
  const [globalEvents] = useState(mockGlobalEvents);
  const [holidays] = useState(mockSchoolHolidays);
  const [tasks, setTasks] = useState<PersonalTask[]>(mockPersonalTasks);

  const allEntries: CalendarEntry[] = useMemo(() => [...globalEvents, ...holidays, ...tasks], [globalEvents, holidays, tasks]);

  const monthLabel = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const goToToday = () => {
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(null);
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setTaskModalOpen(true);
  };

  const handleSaveTask = (data: Omit<PersonalTask, 'id' | 'completed' | 'color' | 'type'>) => {
    if (editingTask) {
      setTasks((prev) => prev.map((t) => t.id === editingTask.id ? { ...t, ...data } : t));
      setEditingTask(null);
    } else {
      const newTask: PersonalTask = {
        ...data,
        id: `pt-${++taskIdCounter}`,
        completed: false,
        color: '#22C55E',
        type: 'task',
      };
      setTasks((prev) => [...prev, newTask]);
    }
  };

  const handleTaskToggle = (taskId: string) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, completed: !t.completed } : t));
  };

  const handleDeleteTask = (taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const toggleType = (type: 'global' | 'holiday' | 'task') => {
    setVisibleTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  return (
    <AppShell
      pageTitle={t('dashboard.schoolCalendar')}
      pageSubtitle={t('dashboard.schoolCalendarSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), onClick: () => navigate('/dashboard/admin') },
        { label: t('dashboard.schoolCalendar') },
      ]}
      notifications={notifications}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Filters */}
        <div className="w-full lg:w-[240px] flex-shrink-0">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 sticky top-4">
            <h3 className="font-label font-semibold text-foreground-800 text-sm mb-4">{t('common.filter')}</h3>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={visibleTypes.global}
                  onChange={() => toggleType('global')}
                  className="w-4 h-4 rounded border-2 border-background-300 text-blue-500 focus:ring-blue-400 cursor-pointer accent-blue-500"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0" />
                  <span className="text-sm font-label text-foreground-700 group-hover:text-foreground-900 transition-colors">{t('dashboard.globalEvents')}</span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={visibleTypes.holiday}
                  onChange={() => toggleType('holiday')}
                  className="w-4 h-4 rounded border-2 border-background-300 text-red-500 focus:ring-red-400 cursor-pointer accent-red-500"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-sm font-label text-foreground-700 group-hover:text-foreground-900 transition-colors">{t('dashboard.schoolHolidays')}</span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={visibleTypes.task}
                  onChange={() => toggleType('task')}
                  className="w-4 h-4 rounded border-2 border-background-300 text-green-500 focus:ring-green-400 cursor-pointer accent-green-500"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-sm font-label text-foreground-700 group-hover:text-foreground-900 transition-colors">{t('dashboard.personalTasks')}</span>
                </div>
              </label>
            </div>

            {/* Legend */}
            <div className="mt-5 pt-4 border-t border-background-200/70">
              <h4 className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide mb-3">Legend</h4>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-foreground-600 font-label">
                  <span className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0" /> Global Awareness
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-600 font-label">
                  <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" /> School Holiday
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-600 font-label">
                  <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" /> Personal Task
                </div>
              </div>
            </div>

            {/* Admin settings link */}
            {canManage('schoolCalendar') && (
              <div className="mt-5 pt-4 border-t border-background-200/70">
                <button
                  onClick={() => navigate('/dashboard/admin/settings?tab=calendar')}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-background-100 rounded-lg text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <i className="ri-settings-4-line text-base" />
                  {t('dashboard.calendarSettings')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Calendar Area */}
        <div className="flex-1 min-w-0">
          {/* Top Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            {/* Left: Month navigation */}
            <div className="flex items-center gap-2">
              <button onClick={goToPrevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer" title={t('dashboard.previousMonth')}>
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <h2 className="font-heading font-700 text-foreground-900 text-lg min-w-[200px] text-center">{monthLabel}</h2>
              <button onClick={goToNextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer" title={t('dashboard.nextMonth')}>
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
              <button onClick={goToToday} className="ml-2 px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                {t('dashboard.goToToday')}
              </button>
            </div>

            {/* Right: Date picker + View toggle */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-sm text-foreground-400" />
                <input
                  type="date"
                  onChange={(e) => {
                    if (e.target.value) {
                      const d = new Date(e.target.value);
                      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                      setSelectedDate(e.target.value);
                    }
                  }}
                  className="text-sm bg-background-50 border border-background-200 rounded-lg px-3 py-1.5 outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
                />
              </div>

              {/* View toggle */}
              <div className="inline-flex gap-1 p-1 bg-background-200/60 rounded-full">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label font-medium transition-all cursor-pointer whitespace-nowrap ${
                    viewMode === 'calendar' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-calendar-2-line text-sm" />
                  {t('dashboard.calendarView')}
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label font-medium transition-all cursor-pointer whitespace-nowrap ${
                    viewMode === 'list' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-list-check-2 text-sm" />
                  {t('dashboard.listView')}
                </button>
              </div>
            </div>
          </div>

          {/* Calendar / List Content */}
          <div className="page-enter">
            {viewMode === 'calendar' ? (
              <CalendarGrid
                currentMonth={currentMonth}
                entries={allEntries}
                visibleTypes={visibleTypes}
                onDateClick={handleDateClick}
                onTaskToggle={handleTaskToggle}
                selectedDate={selectedDate}
              />
            ) : (
              <CalendarList
                entries={allEntries}
                visibleTypes={visibleTypes}
                selectedDate={selectedDate}
                onTaskToggle={handleTaskToggle}
                onDeleteTask={handleDeleteTask}
              />
            )}
          </div>
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => { setTaskModalOpen(false); setEditingTask(null); }}
        onSave={handleSaveTask}
        selectedDate={selectedDate || new Date().toISOString().split('T')[0]}
        existingTask={editingTask}
      />
    </AppShell>
  );
}